/**
 * 재생 증빙 리포트.
 *
 * 이 화면의 독자는 운영자가 아니라 **광고주와 감사**다. 그래서 "무엇이 몇 번,
 * 얼마나 나갔는가"를 숫자로 보이고, 그대로 내보낼 수 있어야 한다.
 */
import { useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowLeft, Printer } from 'lucide-react';
import type { ReportAxis, SignageOneClient } from '../api/client.js';
import { useBrand } from '../components/brand.js';
import { Empty, ErrorNote, formatSeconds, Loading, Panel } from '../components/ui.js';
import { useAsync } from '../hooks.js';
import { browserTimeZone, dayEndIso, dayStartIso, localDate } from '../components/localDate.js';
import { DateField } from '../components/DateField.js';
import { ReportSheet, SHEET_SECTIONS, type SheetData } from './ReportSheet.js';
import { Pager, usePaging } from '../components/Pager.js';
import { paginate } from '../components/paging.js';
import { useUrlQuery } from '../components/urlState.js';

interface Props {
  client: SignageOneClient;
}

/** 기본 조회 기간: 최근 30일. 정산 주기가 대개 월 단위다. */
function defaultRange(): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to.getTime() - 30 * 86_400_000);
  return { from: localDate(from), to: localDate(to) };
}

/**
 * 조회 조건.
 *
 * **고른 날짜는 보는 사람의 하루다.** 예전에는 `T00:00:00Z` 를 붙여 UTC 하루로 물어, 한국에서는
 * "9월 15일" 이 15일 오전 9시부터 16일 오전 9시까지였다 — 전날 밤의 재생이 빠지고 다음 날 아침이
 * 섞였다. 일자별 묶음도 같은 시간대로 자르게 `tz` 를 함께 보낸다.
 */
export function reportQuery(
  from: string,
  to: string,
  groupBy: string,
  tz: string,
): { from: string; to: string; group_by: string; tz: string } {
  return { from: dayStartIso(from), to: dayEndIso(to), group_by: groupBy, tz };
}

/** 집계 축 — 고르는 순서는 홍보팀이 자주 묻는 순서다. */
const AXES: ReadonlyArray<{ value: ReportAxis; label: string; head: string }> = [
  { value: 'asset', label: '콘텐츠별', head: '콘텐츠' },
  { value: 'campaign', label: '캠페인별', head: '캠페인' },
  { value: 'location', label: '위치별', head: '위치' },
  { value: 'display', label: '화면별', head: '화면' },
  { value: 'playlist', label: '플레이리스트별', head: '플레이리스트' },
  { value: 'day', label: '일자별', head: '일자' },
];

export function ReportsScreen({ client }: Props): ReactNode {
  const initial = defaultRange();
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [groupBy, setGroupBy] = useState<ReportAxis>('asset');
  const [search, setSearch] = useSearchParams();
  const viewingSheet = search.get('view') === 'sheet';
  const tz = browserTimeZone();
  const params = reportQuery(from, to, groupBy, tz);

  const report = useAsync(() => client.proofOfPlay(params), [client, from, to, groupBy]);
  /* 쪽 나누기 — 기간·집계 기준을 바꾸면 1쪽으로(`Pager`). 막대의 길이는 쪽이 아니라 전체의 1등에 맞춘다. */
  const url = useUrlQuery();
  const paging = usePaging(url, 'reports', JSON.stringify([from, to, groupBy]));

  const exportQuery = new URLSearchParams({ from: params.from, to: params.to, tz }).toString();
  const csvUrl = client.mediaUrl(`/api/v1/reports/proof-of-play.csv?${exportQuery}`);
  const xlsxUrl = client.mediaUrl(`/api/v1/reports/proof-of-play.xlsx?${exportQuery}`);

  if (viewingSheet) {
    return <SheetView client={client} from={from} to={to} tz={tz} onBack={() => setSearch({})} />;
  }

  const rows = report.data?.rows ?? [];
  const maxPlays = rows.reduce((max, row) => Math.max(max, row.plays), 0);
  const pageSlice = paginate(rows, paging.page, paging.size);
  const axis = AXES.find((item) => item.value === groupBy) ?? AXES[0]!;

  return (
    <div className="screen">
      {report.error ? <ErrorNote message={report.error} /> : null}

      <Panel
        title="재생 증빙"
        className="screen-fill"
        help="화면이 실제로 재생한 기록을 집계합니다. 엑셀 보고서·인쇄용 보고서로 윗선·광고주에 내고, 재생 한 번씩의 원자료는 CSV 로 받습니다."
        actions={
          <>
            <DateField value={from} onChange={setFrom} label="시작일" />
            <span className="cell-muted">~</span>
            <DateField value={to} onChange={setTo} label="종료일" />
            <select
              className="input"
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value as ReportAxis)}
              aria-label="집계 기준"
            >
              {AXES.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
            <button type="button" className="btn" onClick={() => setSearch({ view: 'sheet' })}>
              <Printer className="h-3.5 w-3.5" aria-hidden="true" /> 인쇄용 보고서
            </button>
            <a className="btn" href={xlsxUrl} download>
              엑셀 보고서
            </a>
            <a className="btn" href={csvUrl} download title="재생 한 번이 한 줄인 원자료 — 기간 전체를 자르지 않고 냅니다">
              CSV 원자료
            </a>
          </>
        }
      >
        {report.loading && !report.data ? <Loading /> : null}

        {report.data ? (
          <>
            <div className="stats">
              <div className="stat">
                <div className="stat-value">{report.data.totals.plays.toLocaleString('ko-KR')}</div>
                <div className="stat-label">총 재생 횟수</div>
              </div>
              <div className="stat">
                <div className="stat-value">{formatSeconds(report.data.totals.seconds)}</div>
                <div className="stat-label">총 노출 시간</div>
              </div>
              <div className="stat">
                <div className="stat-value">{report.data.totals.displays.toLocaleString('ko-KR')}</div>
                <div className="stat-label">재생한 화면</div>
              </div>
            </div>

            {groupBy === 'campaign' ? (
              <p className="hint">
                캠페인 기간 안에 그 캠페인의 플레이리스트로 나온 재생입니다. 같은 플레이리스트를 쓰는 캠페인끼리
                기간이 겹치면 양쪽에 다 들어갑니다.
              </p>
            ) : null}

            {rows.length === 0 ? (
              <Empty
                title="이 기간에 재생 기록이 없습니다"
                hint={
                  groupBy === 'campaign'
                    ? '이 기간에 걸친 캠페인이 없거나, 캠페인 플레이리스트가 재생되지 않았습니다.'
                    : '화면의 온라인 상태와 편성 배정을 확인하세요.'
                }
              />
            ) : (
              <>
              <div className="table-scroll">
                <table className="table min-w-[36rem]">
                  <thead>
                    <tr>
                      <th>{axis.head}</th>
                      <th className="col-num">재생 횟수</th>
                      <th className="col-num">노출 시간</th>
                      <th className="col-num">화면 수</th>
                      <th className="col-bar" />
                    </tr>
                  </thead>
                  <tbody>
                    {pageSlice.rows.map((row) => (
                      <tr key={row.key || row.label}>
                        <td className="cell-strong">{row.label}</td>
                        <td className="col-num">{row.plays.toLocaleString('ko-KR')}</td>
                        <td className="col-num cell-muted">{formatSeconds(row.seconds)}</td>
                        <td className="col-num cell-muted">{row.displays.toLocaleString('ko-KR')}</td>
                        <td className="col-bar">
                          {/* 막대는 순위를 눈으로 잡기 위한 것이다 — 정확한 값은 왼쪽 숫자가 준다. */}
                          <div
                            className="bar"
                            style={{ width: `${maxPlays === 0 ? 0 : (row.plays / maxPlays) * 100}%` }}
                            aria-hidden="true"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {report.data.truncated ? (
                  <p className="hint">
                    상위 {rows.length.toLocaleString('ko-KR')}줄만 보입니다. 위의 합계는 기간 전체이고, 전부 보려면 엑셀
                    보고서를 받으세요.
                  </p>
                ) : null}
              </div>
              {/* 표의 가로 스크롤 밖에 둔다 — 좁은 화면에서 표를 옆으로 밀어도 쪽 단추는 제자리에. */}
              <Pager slice={pageSlice} paging={paging} unit="줄" />
              </>
            )}
          </>
        ) : null}
      </Panel>
    </div>
  );
}

/** 인쇄용 보고서 — 축 넷을 한 번에 읽어 A4 에 싣는다. */
function SheetView({
  client,
  from,
  to,
  tz,
  onBack,
}: {
  client: SignageOneClient;
  from: string;
  to: string;
  tz: string;
  onBack: () => void;
}): ReactNode {
  const { brand } = useBrand();
  const sheet = useAsync(async (): Promise<SheetData> => {
    const results = await Promise.all(SHEET_SECTIONS.map((section) => client.proofOfPlay(reportQuery(from, to, section.axis, tz))));
    const sections = Object.fromEntries(SHEET_SECTIONS.map((section, index) => [section.axis, results[index]!.rows])) as SheetData['sections'];
    return { totals: results[0]!.totals, sections };
  }, [client, from, to, tz]);

  return (
    <div className="screen">
      <div className="ins-bar no-print">
        <div className="ins-progress">
          <button type="button" className="btn" onClick={onBack}>
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" /> 리포트로 돌아가기
          </button>
        </div>
        <div className="ins-actions">
          <button type="button" className="btn btn-primary" onClick={() => window.print()} disabled={!sheet.data}>
            <Printer className="h-3.5 w-3.5" aria-hidden="true" /> 인쇄 · PDF 저장
          </button>
        </div>
      </div>
      <p className="hint no-print ins-help">
        인쇄 창에서 용지 <b>A4</b>, 배율 <b>기본(100%)</b>을 확인하세요. 대상(프린터)을 <b>PDF로 저장</b>으로 바꾸면 파일로 남습니다.
      </p>
      {sheet.error ? <ErrorNote message={sheet.error} /> : null}
      {sheet.loading && !sheet.data ? <Loading /> : null}
      {sheet.data ? <ReportSheet data={sheet.data} from={from} to={to} tz={tz} brand={brand} /> : null}
    </div>
  );
}
